import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { getToken } = useClerkAuth();
  const { signOut: clerkSignOut } = useClerk();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(false);

  // Sync Clerk user with backend
  const syncUserWithBackend = useCallback(async () => {
    if (!isSignedIn || !clerkUser) {
      setUser(null);
      setLoading(false);
      setSynced(true);
      return;
    }

    try {
      const token = await getToken();
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Try to get user from backend
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSynced(true);
    } catch (error) {
      console.error('Failed to sync user with backend:', error);
      
      // If user doesn't exist in backend, they need to complete registration
      if (error.response?.status === 404 || error.response?.status === 401) {
        // User exists in Clerk but not in backend - need role selection
        setUser({
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress,
          name: clerkUser.fullName || clerkUser.firstName || clerkUser.primaryEmailAddress?.emailAddress?.split('@')[0],
          role: 'pending',
          clerk_id: clerkUser.id
        });
        setSynced(true);
      }
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, clerkUser, getToken]);

  // Set up axios interceptor for Clerk token
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        if (isSignedIn) {
          try {
            const token = await getToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (error) {
            console.error('Failed to get Clerk token:', error);
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [isSignedIn, getToken]);

  // Sync when Clerk user changes
  useEffect(() => {
    if (clerkLoaded) {
      syncUserWithBackend();
    }
  }, [clerkLoaded, isSignedIn, clerkUser, syncUserWithBackend]);

  const logout = async () => {
    try {
      await clerkSignOut();
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setSynced(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const refreshUser = async () => {
    await syncUserWithBackend();
  };

  const value = {
    user,
    setUser,
    loading: loading || !clerkLoaded,
    logout,
    isAuthenticated: !!user && user.role !== 'pending',
    isPending: user?.role === 'pending',
    refreshUser,
    clerkUser,
    isSignedIn,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
